import React, { useState, useRef, useEffect, useMemo } from "react";
import { Formik } from "formik";
import { connect, useSelector, shallowEqual } from "react-redux";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { injectIntl } from "react-intl";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { TextField } from "@material-ui/core";
import { FormControl } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useDropzone } from "react-dropzone";
import "./index.scss";
import SVG from "react-inlinesvg";
import { reportFault } from "./reportCrud";
import Upload from "../../Upload";
import LoadingAnimation from "../../../components/LoadingAnimation";
import AddObservation from "../ServiceRequest/Observation/ReportObservation/AddObservation";
import EditObservation from "../ServiceRequest/Observation/ReportObservation/EditObservation";
import { ReactTable } from "../Assets/AssetList/table/Table";
import ScanQrButton from "../../../components/ScanQrButton";
import { publicFaultScanRequest } from "../../../components/publicFaultQr";

// An asset identified by the reusable QR scanner (or the Asset ID fallback),
// shaped like the other picker options. The scanner only identifies the
// asset; the report itself is validated by the same endpoint as always.
const toScannedOption = asset => ({
    _id: asset._id,
    type: "asset",
    label: asset.name || asset.assetID || "Unnamed asset",
    assetID: asset.assetID || "",
    sublabel: [asset.site && asset.site.name, asset.locationName].filter(Boolean).join(" \u00b7 "),
    raw: asset
});

const FAULT_TYPES = [
    { name: "Repair", label: "Repair" },
    { name: "Replacement", label: "Replacement" }
];

// Normalizes an Asset document (from GET /v2/asset/asset-owner/:id) into the
// shape the search combobox + submit logic below expect.
const toAssetOption = asset => ({
    _id: asset._id,
    type: "asset",
    label: asset.name || asset.assetID || "Unnamed asset",
    assetID: asset.assetID || "",
    sublabel: [
        asset.clusterName && asset.clusterName.name,
        asset.locationName || asset.locationID,
        asset.address
    ]
        .filter(Boolean)
        .join(" \u00b7 "),
    raw: asset
});

// Normalizes a Cluster document into the same shape, so both can live in one
// searchable list - this is what lets a user report a fault without first
// having to know (or even care) whether the item belongs to a cluster.
const toClusterOption = cluster => ({
    _id: cluster._id,
    type: "cluster",
    label: cluster.name || cluster.clusterID || "Unnamed site",
    assetID: cluster.clusterID || "",
    sublabel: cluster.address || "Whole site / cluster",
    raw: cluster
});

// Normalizes the `faultDetails` route-state object that ViewAssignedAsset.js /
// ViewAssignedCluster.js pass when a logged-in user clicks "Report Fault" on a
// specific asset or cluster they're already viewing (see those files' own
// `setFaultRoute(...)` calls for the exact shape, including the `isCluster`
// flag added alongside this redesign to fix a pre-existing mix-up where
// cluster-originated reports were silently submitted as if they were assets).
const toPrefilledOption = faultDetails => {
    if (!faultDetails) return null;
    return {
        _id: faultDetails._id,
        type: faultDetails.isCluster ? "cluster" : "asset",
        label: faultDetails.name || faultDetails.newName || "",
        assetID: faultDetails.assetID || "",
        sublabel: faultDetails.responsibleTechnician
            ? `Technician: ${faultDetails.responsibleTechnician.name}`
            : "",
        raw: faultDetails
    };
};

function TargetOptionLabel(option, { context }) {
    const isCluster = option.type === "cluster";

    if (context === "value") {
        // Compact chip label for the closed multi-select control - the full
        // detail list is shown separately just below the field for whatever
        // has been selected so far.
        return <span>{option.label}</span>;
    }

    return (
        <div className="fault-target-option">
            <span
                className={`fault-target-option__badge fault-target-option__badge--${
                    isCluster ? "cluster" : "asset"
                }`}
            >
                {isCluster ? "Site" : "Asset"}
            </span>
            <div className="fault-target-option__body">
                <div className="fault-target-option__name">
                    {option.label}
                    {option.assetID ? ` \u00b7 ${option.assetID}` : ""}
                </div>
                {option.sublabel && (
                    <div className="fault-target-option__meta">{option.sublabel}</div>
                )}
            </div>
        </div>
    );
}

// The picker asks the server for one page of matches as the reporter types (the register can hold thousands of
// assets); the group headings say when there are more than the ones shown.
const buildTargetGroups = data => {
    const assets = ((data && data.asset) || []).map(toAssetOption);
    const clusters = ((data && data.cluster) || []).map(toClusterOption);
    const heading = (name, shown, total) => `${name} (${shown}${total > shown ? ` of ${total} \u2013 type to narrow down` : ""})`;
    return [
        { label: heading("Assets", assets.length, (data && data.assetTotal) || 0), options: assets },
        { label: heading("Sites & clusters", clusters.length, (data && data.clusterTotal) || 0), options: clusters }
    ].filter(group => group.options.length > 0);
};

function ReportPageForm(props) {
    const location = useLocation();

    // `embedded` is set by FaultReportForm.js when this same form is
    // rendered as a normal page inside the authenticated dashboard (see
    // that file's docblock) rather than as the standalone, unauthenticated
    // `/report/fault/:id` page (app/Routes.js) - the two need slightly
    // different chrome: embedded mode drops the standalone page's own
    // logo/illustration/copyright (redundant next to the dashboard's own
    // navigation) and sends "Back" to the fault portal page instead of
    // `/auth`, but otherwise shares 100% of the same form/stepper logic.
    const { intl, id, embedded } = props;

    const [initialTargetOptions, setInitialTargetOptions] = useState([]);
    const [loadingTargets, setLoadingTargets] = useState(true);
    const [targetsError, setTargetsError] = useState(false);

    const [photoFile, setPhotoFile] = useState(null);
    // The uploaded photo's URL, captured once (when moving from "details" to
    // "observations") and reused at final-submit time, so re-entering the
    // observations step doesn't re-upload the same file.
    const [submittedDocumentUrl, setSubmittedDocumentUrl] = useState("");

    const [loading, setLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const scrollup = useRef(null);

    // Logged-in users go through a second, in-page step ("observations")
    // before the fault report is actually created - anonymous/public
    // reporters have no reason to see this step (there's no internal
    // service-charge workflow relevant to them) and submit directly from
    // the "details" step. This used to be a full page navigation to
    // /service-request/fault (AddFaultObservation.js) - moved in-page here
    // so a logged-in user filling this out doesn't lose their place (and so
    // the public/anonymous flow, which never navigated there, is unaffected).
    //
    // Unlike an earlier revision of this, there is NO separate "pendingReport"
    // snapshot of the step-1 fields anymore - the observations step reads
    // straight from the SAME live Formik `values`, and (per the redesign)
    // those same fields are rendered as an ACTUAL EDITABLE FORM on the left
    // illustration panel while step is "observations" (see the `step ===
    // "observations"` branches below and in the left-panel JSX) so a user
    // can revise their selection/description/contact details and have it
    // reflect immediately in what's about to be submitted - editing them
    // there is editing the exact same Formik state the submit button reads.
    const [step, setStep] = useState("details"); // "details" | "observations"
    // Observations are per-TARGET, not one shared list - a reporter picking
    // several assets/sites might only know parts/labour needed for some of
    // them, not all (per design: "it's OK if they do not put any
    // observation for any of the assets"). Keyed by each target's `_id`
    // (the asset/cluster's own _id, which is unique across the whole
    // selection), value is that target's own observations array.
    const [observationsByTarget, setObservationsByTarget] = useState({});
    // Which target's observations table/modal is currently active - kept in
    // sync directly at every point the target selection changes (see the
    // target Select's onChange/Remove handlers below), rather than via a
    // useEffect watching a snapshot, since there's no snapshot anymore.
    const [activeTargetId, setActiveTargetId] = useState(null);
    const [addListOfObservation, setAddListOfObservaion] = useState(false);
    const [editListOfObservation, setEditListOfObservaion] = useState(false);
    const [observationDataToBeEdited, setObservationDataToBeEdited] = useState(null);

    const { user } = useSelector(state => state && state.auth, shallowEqual);
    const isLoggedIn = !!(user && user.user);

    useEffect(() => {
        let mounted = true;
        setLoadingTargets(true);
        setTargetsError(false);

        axios
            .get(`/v2/asset/asset-owner/${id}`, { params: { limit: 30 } })
            .then(({ data }) => {
                if (!mounted) return;
                setInitialTargetOptions(buildTargetGroups(data));
                setLoadingTargets(false);
            })
            .catch(() => {
                if (!mounted) return;
                setTargetsError(true);
                setLoadingTargets(false);
            });

        return () => {
            mounted = false;
        };
    }, [id]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: false,
        accept: ".png,.jpg,.jpeg",
        onDrop: accepted => {
            if (accepted && accepted[0]) setPhotoFile(accepted[0]);
        }
    });

    const executeScroll = () => scrollup.current.scrollIntoView();

    // Keeps `activeTargetId` pointing at a target that still exists in the
    // (possibly just-edited) selection - called from both the target
    // Select's onChange and each chip's "Remove" button below.
    const syncActiveTarget = nextTargets => {
        setActiveTargetId(prevId => {
            if (nextTargets.some(t => t._id === prevId)) return prevId;
            return nextTargets.length ? nextTargets[0]._id : null;
        });
    };

    // Columns for the optional "Observations" table shown in step 2
    // (logged-in users only) - same shape/behavior as the table this
    // replaces in the old AddFaultObservation.js page, but scoped to
    // whichever target is currently active (see `activeTargetId`).
    const activeObservations = (activeTargetId && observationsByTarget[activeTargetId]) || [];
    const observationColumns = useMemo(
        () => [
            { Header: "Service Type/Charge", accessor: "serviceType" },
            { Header: "Products", accessor: "product" },
            { Header: "Brands/Model", accessor: "brand" },
            { Header: "Quantity", accessor: "quantity" },
            { Header: "Charge/Unit", accessor: "charge" },
            { Header: "Tax%", accessor: "tax" },
            { Header: "Total", accessor: "total" },
            {
                Header: "Actions",
                accessor: "action",
                Cell: ({ row }) => (
                    <div style={{ display: "flex" }}>
                        <button
                            title="Remove Observation"
                            type="button"
                            className="btn btn-icon btn-light btn-hover-primary btn-sm"
                            onClick={() => {
                                setObservationsByTarget(prev => {
                                    const next = [...(prev[activeTargetId] || [])];
                                    next.splice(row.index, 1);
                                    return { ...prev, [activeTargetId]: next };
                                });
                            }}
                        >
                            <span className="svg-icon svg-icon-md svg-icon-primary">
                                <SVG title="Remove Observation" src={toAbsoluteUrl("/media/svg/icons/General/Trash.svg")} />
                            </span>
                        </button>
                        <button
                            title="Edit Observation"
                            type="button"
                            className="btn btn-icon btn-light btn-hover-primary btn-sm mx-3"
                            onClick={() => {
                                setEditListOfObservaion(true);
                                setObservationDataToBeEdited(row);
                            }}
                        >
                            <span className="svg-icon svg-icon-md svg-icon-primary">
                                <SVG title="Edit Observation" src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")} />
                            </span>
                        </button>
                    </div>
                )
            }
        ],
        [activeTargetId]
    );

    // Final submission for logged-in users, fired from the observations
    // step. Reads straight from the live Formik `values` (passed in from the
    // render-prop below) rather than a frozen snapshot, so anything edited
    // via the left-panel form while on this step - including the target
    // selection itself - is reflected in what actually gets submitted. Per
    // the design brief: when multiple assets/sites are selected, this is
    // submitted as ONE combined report (not one report per target) so the
    // asset owner sees a single item to act on, with each target's own
    // (optional) observations attached to it.
    const submitFinalReport = values => {
        if (!values.targets || values.targets.length === 0) return;

        setLoading(true);

        reportFault({
            fullName: values.fullName,
            email: values.email,
            phoneNumber: values.phoneNumber,
            description: values.description,
            faultType: (values.faultType && values.faultType.name) || null,
            document: [`${submittedDocumentUrl}`],
            // IMPORTANT: POST /v2/fault/report does `Asset.findById(asset)`
            // / `Cluster.findById(cluster)` server-side per target - it
            // needs each target's plain id string, NOT the full raw asset/
            // cluster object. Sending the raw object here (a bug introduced
            // in an earlier revision of this flow) silently made `findById`
            // match nothing, leaving `ownedBy` null and the endpoint 500ing
            // with "Asset owner not found" - surfaced to the user as
            // "Something went wrong submitting your report. Please try
            // again."
            targets: values.targets.map(target => {
                const isCluster = target.type === "cluster";
                return {
                    isCluster,
                    observations: observationsByTarget[target._id] || [],
                    ...(isCluster ? { cluster: target._id } : { asset: target._id })
                };
            })
        })
            .then(() => {
                setLoading(false);
                setIsSuccess(true);
                setStep("details");
                setObservationsByTarget({});
                setActiveTargetId(null);
                setPhotoFile(null);
                setSubmittedDocumentUrl("");
                executeScroll();
            })
            .catch(() => {
                setLoading(false);
                setIsError(true);
            });
    };

    const loadTargetOptions = input =>
        axios
            .get(`/v2/asset/asset-owner/${id}`, { params: { limit: 30, search: input || undefined } })
            .then(({ data }) => buildTargetGroups(data))
            .catch(() => []);

    const hasNoTargets = !loadingTargets && !targetsError && initialTargetOptions.length === 0;

    const prefilledTarget = useMemo(
        () => toPrefilledOption(location && location.state && location.state.faultDetails),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    // Memoized (not recomputed every render) and NOT paired with Formik's
    // `enableReinitialize` - that combination was resetting every field
    // (including whatever the user had already selected/typed) on every
    // parent re-render, since a brand-new `initialValues` object identity
    // made Formik treat it as "the initial values changed" each time. This
    // was the root cause of the target/fault-type selections appearing to
    // silently revert and the form feeling unstable while interacting.
    const initialValues = useMemo(
        () => ({
            email: (user && user.user && user.user.email) || "",
            fullName: user && user.user ? `${user.user.firstName || ""} ${user.user.lastName || ""}`.trim() : "",
            phoneNumber: (user && user.user && user.user.telephone) || "",
            // Array - the picker supports selecting several assets/sites at
            // once (isMulti below) so a single report can cover multiple
            // affected items in one submission.
            targets: prefilledTarget ? [prefilledTarget] : [],
            faultType: null,
            description: ""
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <>
            {loading && <LoadingAnimation info="Submitting" />}
            <div className={embedded ? "reportContainer reportContainer--embedded" : "reportContainer bg-white"}>
                <Formik
                    initialValues={initialValues}
                    validate={values => {
                        const errors = {};

                        if (!values.targets || values.targets.length === 0) {
                            errors.targets = "Select at least one asset or site this report is about";
                        }

                        if (!values.faultType) {
                            errors.faultType = "Select a fault type";
                        }

                        if (!values.description) {
                            errors.description = intl.formatMessage({
                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                            });
                        }

                        if (!values.fullName) {
                            errors.fullName = intl.formatMessage({
                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                            });
                        }

                        if (!values.email) {
                            errors.email = intl.formatMessage({
                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                            });
                        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
                            errors.email = intl.formatMessage({
                                id: "AUTH.VALIDATION.INVALID_FIELD"
                            });
                        }

                        if (!values.phoneNumber) {
                            errors.phoneNumber = intl.formatMessage({
                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                            });
                        }

                        return errors;
                    }}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                        setLoading(true);
                        let doc = "";

                        if (photoFile) {
                            doc = await Upload(photoFile);

                            if (doc == null) {
                                setSubmitting(false);
                                setIsError(true);
                                setLoading(false);
                                return;
                            }
                        }

                        // Normalize each selected target (asset or cluster) into a
                        // descriptor carrying everything the anonymous submission
                        // path below needs.
                        const normalizedTargets = values.targets.map(target => ({
                            isCluster: target.type === "cluster",
                            id: target._id
                        }));

                        setSubmitting(true);

                        if (isLoggedIn) {
                            // Move to the in-page "observations" step instead of
                            // navigating to a separate route - keeps the user on
                            // this same page/URL so they never lose their place
                            // (this used to push to /service-request/fault,
                            // which - besides the unnecessary navigation - had
                            // no meaning for anonymous public reporters and was
                            // therefore only ever reached by logged-in users).
                            setSubmitting(false);
                            setLoading(false);
                            setSubmittedDocumentUrl(doc);
                            syncActiveTarget(values.targets);
                            setStep("observations");
                            executeScroll();
                            return;
                        }

                        const commonFields = {
                            fullName: values.fullName,
                            email: values.email,
                            phoneNumber: values.phoneNumber,
                            description: values.description,
                            faultType: (values.faultType && values.faultType.name) || null,
                            document: [`${doc}`]
                        };

                        // Anonymous public submissions go straight to
                        // POST /v2/fault/report, which only accepts ONE
                        // asset/cluster id per call - so a multi-target report
                        // becomes one fault record per selected target, all
                        // sharing the same description/contact/photo.
                        Promise.all(
                            normalizedTargets.map(target =>
                                reportFault({
                                    ...commonFields,
                                    isCluster: target.isCluster,
                                    ...(target.isCluster ? { cluster: target.id } : { asset: target.id })
                                })
                            )
                        )
                            .then(() => {
                                setSubmitting(false);
                                setIsSuccess(true);
                                setPhotoFile(null);
                                resetForm({});
                                setLoading(false);
                                executeScroll();
                            })
                            .catch(() => {
                                setSubmitting(false);
                                setIsError(true);
                                setLoading(false);
                            });
                    }}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                        setFieldTouched
                    }) => {
                        // The exact same fields, rendered in ONE of two
                        // possible locations depending on `step` (never
                        // both at once): inline in the right-hand card on
                        // step 1, or in the left illustration panel (as a
                        // live, still-editable form) on step 2 - see the
                        // render below. Because both locations share this
                        // one Formik render-prop closure, editing a field
                        // in either location updates the exact same
                        // `values` the final submit button reads.
                        const detailsFields = (
                            <>
                                <section className="fault-report-section">
                                    <div className="fault-report-section__label">
                                        <span className="fault-report-section__num">1</span>
                                        What needs attention?
                                    </div>
                                    <p className="fault-report-section__hint">
                                        Search by name, ID, or site &mdash; you don&rsquo;t need to
                                        know which cluster or location it belongs to. You can
                                        select more than one if several assets or sites are
                                        affected.
                                    </p>

                                    <div className="mb-3">
                                        <ScanQrButton
                                            title="Scan asset QR"
                                            label={isLoggedIn ? "Scan QR or enter Asset ID" : "Scan QR"}
                                            className="btn btn-light-primary btn-sm"
                                            requires={isLoggedIn ? "manageAssetFault" : undefined}
                                            publicRequest={isLoggedIn ? undefined : publicFaultScanRequest(id)}
                                            onAsset={asset => {
                                                if (values.targets.some(t => t._id === asset._id)) return;
                                                const nextTargets = [...values.targets, toScannedOption(asset)];
                                                setFieldValue("targets", nextTargets);
                                                syncActiveTarget(nextTargets);
                                            }}
                                        />
                                    </div>

                                    {loadingTargets && (
                                        <div className="fault-report-inline-status">Loading assets&hellip;</div>
                                    )}

                                    {targetsError && (
                                        <div className="fault-report-inline-status fault-report-inline-status--error">
                                            Couldn&rsquo;t load the asset list. Please refresh the page.
                                        </div>
                                    )}

                                    {hasNoTargets && values.targets.length === 0 && (
                                        <div className="fault-report-inline-status">
                                            No assets or sites are registered for this link yet.
                                        </div>
                                    )}

                                    {!loadingTargets && !targetsError && (!hasNoTargets || values.targets.length > 0) && (
                                        <>
                                            <AsyncSelect
                                                className="kt-width-full"
                                                classNamePrefix="fault-target-select"
                                                placeholder="Search by asset name, ID, or site name..."
                                                value={values.targets}
                                                name="targets"
                                                isMulti
                                                isClearable
                                                hideSelectedOptions={false}
                                                closeMenuOnSelect={false}
                                                cacheOptions
                                                defaultOptions={initialTargetOptions}
                                                loadOptions={loadTargetOptions}
                                                filterOption={null}
                                                formatOptionLabel={TargetOptionLabel}
                                                getOptionValue={option => option._id}
                                                onChange={options => {
                                                    const nextTargets = options || [];
                                                    setFieldValue("targets", nextTargets);
                                                    syncActiveTarget(nextTargets);
                                                }}
                                                onBlur={() => setFieldTouched("targets", true)}
                                            />
                                            {touched.targets && errors.targets && (
                                                <div className="fault-field-error">{errors.targets}</div>
                                            )}

                                            {values.targets.length > 0 && (
                                                <div className="fault-target-list">
                                                    {values.targets.map(target => (
                                                        <div className="fault-target-summary" key={target._id}>
                                                            <span
                                                                className={`fault-target-summary__badge fault-target-summary__badge--${
                                                                    target.type === "cluster" ? "cluster" : "asset"
                                                                }`}
                                                            >
                                                                {target.type === "cluster" ? "Site" : "Asset"}
                                                            </span>
                                                            <div className="fault-target-summary__body">
                                                                <div className="fault-target-summary__name">
                                                                    {target.label}
                                                                    {target.assetID ? ` \u00b7 ${target.assetID}` : ""}
                                                                </div>
                                                                {target.sublabel && (
                                                                    <div className="fault-target-summary__meta">
                                                                        {target.sublabel}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="fault-target-summary__change"
                                                                onClick={() => {
                                                                    const nextTargets = values.targets.filter(
                                                                        t => t._id !== target._id
                                                                    );
                                                                    setFieldValue("targets", nextTargets);
                                                                    syncActiveTarget(nextTargets);
                                                                }}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </section>

                                <section className="fault-report-section">
                                    <div className="fault-report-section__label">
                                        <span className="fault-report-section__num">2</span>
                                        What&rsquo;s the issue?
                                    </div>

                                    <div className="form-group">
                                        <span>Fault type</span>
                                        <Select
                                            className="kt-width-full"
                                            placeholder="Select..."
                                            closeMenuOnSelect={true}
                                            value={values.faultType}
                                            name="faultType"
                                            options={FAULT_TYPES}
                                            getOptionValue={option => option.name}
                                            getOptionLabel={option => option.label}
                                            onChange={option => setFieldValue("faultType", option)}
                                            onBlur={() => setFieldTouched("faultType", true)}
                                        />
                                        {touched.faultType && errors.faultType && (
                                            <div className="fault-field-error">{errors.faultType}</div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <span>Description</span>
                                        <FormControl
                                            as="textarea"
                                            rows={4}
                                            margin="normal"
                                            className="kt-width-full"
                                            name="description"
                                            placeholder="Describe what's wrong, e.g. 'Not powering on since yesterday morning'"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.description}
                                            isInvalid={Boolean(touched.description && errors.description)}
                                        />
                                        {touched.description && errors.description && (
                                            <div className="fault-field-error">{errors.description}</div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <span>Photo (optional)</span>
                                        <div
                                            {...getRootProps()}
                                            className={`fault-dropzone${
                                                isDragActive ? " fault-dropzone--active" : ""
                                            }`}
                                        >
                                            <input {...getInputProps()} />
                                            {photoFile ? (
                                                <div className="fault-dropzone__file">
                                                    <span>{photoFile.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setPhotoFile(null);
                                                        }}
                                                        aria-label="Remove photo"
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            ) : (
                                                <span>Drag a photo here, or click to browse</span>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                <section className="fault-report-section">
                                    <div className="fault-report-section__label">
                                        <span className="fault-report-section__num">3</span>
                                        Your contact details
                                    </div>

                                    <div className="form-group mb-0">
                                        <TextField
                                            margin="normal"
                                            label="Full name"
                                            className="kt-width-full"
                                            name="fullName"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.fullName}
                                            helperText={touched.fullName && errors.fullName}
                                            error={Boolean(touched.fullName && errors.fullName)}
                                        />
                                    </div>

                                    <div className="form-group mb-0">
                                        <TextField
                                            label="Email"
                                            margin="normal"
                                            className="kt-width-full"
                                            name="email"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.email}
                                            helperText={touched.email && errors.email}
                                            error={Boolean(touched.email && errors.email)}
                                        />
                                    </div>

                                    <div className="form-group mb-0">
                                        <TextField
                                            type="tel"
                                            label="Phone number"
                                            margin="normal"
                                            className="kt-width-full"
                                            name="phoneNumber"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.phoneNumber}
                                            helperText={touched.phoneNumber && errors.phoneNumber}
                                            error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                                        />
                                    </div>
                                </section>
                            </>
                        );

                        return (
                            <>
                                {(!embedded || step === "observations") && (
                                    <div
                                        className={`logoSection${
                                            step === "observations" ? " logoSection--editform" : ""
                                        }`}
                                    >
                                        {!embedded && (
                                            <Link to={isLoggedIn ? "/dashboard" : "/"} className="logoSection__brand">
                                                <img
                                                    src={toAbsoluteUrl("/media/client-logos/logo.png")}
                                                    alt="Anike"
                                                />
                                            </Link>
                                        )}

                                        {step === "observations" ? (
                                            <div className="logoSection__editform">
                                                <h4>Edit your report</h4>
                                                {detailsFields}
                                            </div>
                                        ) : (
                                            !embedded && <img src={toAbsoluteUrl("/media/bg/bg-4.png")} alt="" />
                                        )}

                                        {!embedded && (
                                            <div className="opacity-70 font-weight-bold date">
                                                &copy; {new Date().getFullYear()} Anike
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="formSection">
                                    <div className="fault-report-card">
                                        <div style={{ transform: "translateY(-40px)" }} ref={scrollup}></div>

                                        <form onSubmit={handleSubmit} noValidate autoComplete="off">
                                            <header className="fault-report-card__header">
                                                <h1>{step === "observations" ? "Add observations & submit" : "Report a fault"}</h1>
                                                <p>
                                                    {step === "observations"
                                                        ? "Optionally list any parts, labour, or service charges needed to fix this, then submit your report."
                                                        : "Search for the asset or site that needs attention, describe what\u2019s wrong, and leave your contact details so we can follow up."}
                                                </p>
                                            </header>

                                            {isError && (
                                                <div className="fault-alert fault-alert--error" role="alert">
                                                    <span className="svg-icon svg-icon-3x svg-icon-danger">
                                                        <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
                                                    </span>
                                                    <div className="fault-alert__text">
                                                        Something went wrong submitting your report. Please try
                                                        again.
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="fault-alert__close"
                                                        onClick={() => setIsError(false)}
                                                        aria-label="Dismiss"
                                                    >
                                                        <i className="ki ki-close"></i>
                                                    </button>
                                                </div>
                                            )}

                                            {isSuccess && (
                                                <div className="fault-alert fault-alert--success" role="alert">
                                                    <span className="svg-icon svg-icon-3x svg-icon-success">
                                                        <SVG src={toAbsoluteUrl("/media/svg/icons/Code/Info-circle.svg")} />
                                                    </span>
                                                    <div className="fault-alert__text">
                                                        Your fault report has been submitted successfully. Thank
                                                        you!
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="fault-alert__close"
                                                        onClick={() => setIsSuccess(false)}
                                                        aria-label="Dismiss"
                                                    >
                                                        <i className="ki ki-close"></i>
                                                    </button>
                                                </div>
                                            )}

                                            {step === "details" && (
                                                <>
                                                    {detailsFields}

                                                    <div className="fault-report-actions">
                                                        <Link
                                                            to={embedded ? "/asset/report-fault" : isLoggedIn ? "/dashboard" : "/auth"}
                                                            className="btn btn-secondary font-weight-bold px-9 py-4 my-3"
                                                        >
                                                            Back
                                                        </Link>
                                                        <button
                                                            type="submit"
                                                            disabled={isSubmitting}
                                                            className="btn btn-primary font-weight-bold px-9 py-4 my-3"
                                                        >
                                                            <span>{isLoggedIn ? "Next" : "Submit report"}</span>
                                                        </button>
                                                    </div>
                                                </>
                                            )}

                                            {/*
                                              Step 2 ("observations") - logged-in users only (see
                                              the Formik onSubmit above: anonymous submissions
                                              never set step to "observations"). The fault
                                              details themselves are the editable form rendered
                                              in the left panel above (`detailsFields`) - not
                                              repeated here, since showing a second, read-only
                                              copy of the same information would just be
                                              duplication.
                                            */}
                                            {isLoggedIn && step === "observations" && (
                                                <div>
                                                    <section className="fault-report-section">
                                                        <div className="fault-report-section__label">
                                                            <span className="fault-report-section__num">+</span>
                                                            Observations{" "}
                                                            <span className="fault-report-section__optional">(optional)</span>
                                                        </div>
                                                        <p className="fault-report-section__hint">
                                                            List any parts, labour, or service charges needed to
                                                            fix this for each asset/site &mdash; it&rsquo;s fine to
                                                            leave this empty for some or all of them.
                                                        </p>

                                                        {values.targets.length > 1 && (
                                                            <div className="fault-target-tabs">
                                                                {values.targets.map(target => (
                                                                    <button
                                                                        type="button"
                                                                        key={target._id}
                                                                        className={`fault-target-tabs__tab${
                                                                            activeTargetId === target._id
                                                                                ? " fault-target-tabs__tab--active"
                                                                                : ""
                                                                        }`}
                                                                        onClick={() => setActiveTargetId(target._id)}
                                                                    >
                                                                        {target.label}
                                                                        {(observationsByTarget[target._id] || []).length > 0 && (
                                                                            <span className="fault-target-tabs__count">
                                                                                {observationsByTarget[target._id].length}
                                                                            </span>
                                                                        )}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}

                                                        <div className="fault-observations-table">
                                                            <ReactTable columns={observationColumns} data={activeObservations} />
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => setAddListOfObservaion(true)}
                                                            className="btn btn-secondary mt-3"
                                                        >
                                                            <i className="flaticon2-add-1"></i>&nbsp;
                                                            <span>Add observation</span>
                                                        </button>
                                                    </section>

                                                    <div className="fault-report-actions">
                                                        <button
                                                            type="button"
                                                            className="btn btn-secondary font-weight-bold px-9 py-4 my-3"
                                                            onClick={() => setStep("details")}
                                                        >
                                                            Back
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={loading}
                                                            className="btn btn-primary font-weight-bold px-9 py-4 my-3"
                                                            onClick={() => submitFinalReport(values)}
                                                        >
                                                            <span>Submit report</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </form>
                                    </div>
                                </div>
                            </>
                        );
                    }}
                </Formik>
            </div>

            {addListOfObservation && (
                <AddObservation
                    setAddListOfObservaion={setAddListOfObservaion}
                    setObservationData={next =>
                        setObservationsByTarget(prev => ({ ...prev, [activeTargetId]: next }))
                    }
                    observationData={activeObservations}
                />
            )}

            {editListOfObservation && (
                <EditObservation
                    setEditListOfObservaion={setEditListOfObservaion}
                    setObservationData={next =>
                        setObservationsByTarget(prev => ({ ...prev, [activeTargetId]: next }))
                    }
                    observationData={activeObservations}
                    row={observationDataToBeEdited}
                />
            )}
        </>
    );
}

export default injectIntl(connect(null, null)(ReportPageForm));
